import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './EnquiryInbox.css';

const API = 'https://cctvapi.shopkwality.in/api';

const EnquiryInbox = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [enquiries, setEnquiries] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEnquiries(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchEnquiries = async (searchQuery = '') => {
    try {
      setLoading(true);
      
      let url = `${API}/enquiries`;
      if (searchQuery) {
        url = `${API}/enquiries/search?q=${encodeURIComponent(searchQuery)}`;
      }
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle different response formats
      const enquiryData = res.data?.data || res.data;
      setEnquiries(Array.isArray(enquiryData) ? enquiryData : []);
    } catch (err) {
      console.error('Failed to load enquiries', err);
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;

    try {
      await axios.delete(`${API}/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEnquiries(prev => prev.filter(e => e.id !== id));
      alert('Enquiry deleted successfully');
    } catch (err) {
      console.error('Delete error:', err);
      alert(err?.response?.data?.message || 'Failed to delete enquiry');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': '#FF6B6B',
      'in_progress': '#F5A623',
      'quoted': '#2ECC71',
      'closed': '#7F8C8D'
    };
    return colors[status] || '#7F8C8D';
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="page-header">
          <div className="container">
            <h1 className="page-title">Enquiry Inbox</h1>
            <p className="page-subtitle">Manage customer enquiries and requests</p>
          </div>
        </div>

        <div className="container">
          {/* Search Bar */}
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by name, mobile, or product..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Enquiries List */}
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading enquiries...</p>
            </div>
          ) : enquiries.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔭</div>
              <div className="empty-text">No enquiries found</div>
              <div className="empty-subtext">
                {search ? 'Try a different search term' : 'Enquiries will appear here'}
              </div>
            </div>
          ) : (
            <div className="enquiries-grid">
              {enquiries.map((enquiry) => {
                const itemCount = enquiry.items?.length || enquiry.items_count || enquiry._count?.items || 0;
                const hasQuotation = enquiry.quotation && (Array.isArray(enquiry.quotation) ? enquiry.quotation.length > 0 : !!enquiry.quotation.id);

                return (
                  <div
                    key={enquiry.id}
                    className="enquiry-card"
                    style={{ borderLeftColor: getStatusColor(enquiry.status) }}
                  >
                    <div className="enquiry-header">
                      <div className="enquiry-info">
                        <h3 className="enquiry-name">
                          {enquiry.customer_name || 'Unknown Customer'}
                        </h3>
                        <p className="enquiry-mobile">{enquiry.customer_mobile || '-'}</p>
                      </div>
                      <span
                        className="status-pill"
                        style={{ 
                          backgroundColor: `${getStatusColor(enquiry.status)}20`,
                          color: getStatusColor(enquiry.status)
                        }}
                      >
                        {enquiry.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>

                    {enquiry.message && (
                      <p className="enquiry-message">{enquiry.message}</p>
                    )}

                    <div className="enquiry-meta">
                      <span className="enquiry-items">📦 {itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                      {hasQuotation && <span className="quoted-badge">✓ Quoted</span>}
                    </div>

                    <div className="enquiry-footer">
                      <span className="enquiry-date">
                        {new Date(enquiry.created_at).toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      <div className="enquiry-actions">
                        <button
                          onClick={() => navigate(`/enquiries/${enquiry.id}`)}
                          className="btn btn-sm btn-primary"
                        >
                          View
                        </button>
                        <button
                          onClick={() => deleteEnquiry(enquiry.id)}
                          className="btn btn-sm btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiryInbox;